import type {
  AvailableLawyerDto,
  ClientAssignmentHistoryDto,
  ClientDto,
  ReassignLawyerBody,
  StaffAcceptInviteBody,
  StaffAuthResponse,
  StaffDashboardStatsDto,
  StaffDto,
  StaffForgotPasswordBody,
  StaffLoginBody,
  StaffResetPasswordBody,
  FinishShiftBody,
  FinishShiftResponse,
  MyShiftDto,
  ShiftListDto,
  ShiftListQuery,
  StartShiftResponse,
  LawyerChangeRequestDto,
  LawyerChangeRequestListDto,
  LawyerChangeRequestListQuery,
  ApproveLawyerChangeRequestBody,
  RejectLawyerChangeRequestBody,
  StaffNotificationStateDto,
  StaffTelegramLinkDto,
  UpdateStaffNotificationSettingsBody,
  CaseDetailDto,
  CaseSummaryDto,
  ResponseTemplateDto,
  SaveCaseDraftBody,
  UpdateCaseBody,
  CreateResponseTemplateBody,
  UpdateResponseTemplateBody,
} from '@lex/contracts';
import type { ApiClient } from './client';

export function createStaffAuthApi(client: ApiClient) {
  return {
    login(body: StaffLoginBody): Promise<StaffAuthResponse> {
      return client.post<StaffAuthResponse>('/api/v1/staff/auth/login', body, { auth: false });
    },
    acceptInvite(body: StaffAcceptInviteBody): Promise<StaffAuthResponse> {
      return client.post<StaffAuthResponse>('/api/v1/staff/auth/accept-invite', body, {
        auth: false,
      });
    },
    refresh(refreshToken: string): Promise<StaffAuthResponse> {
      return client.post<StaffAuthResponse>(
        '/api/v1/staff/auth/refresh',
        { refreshToken },
        { auth: false }
      );
    },
    forgotPassword(body: StaffForgotPasswordBody): Promise<{ ok: true }> {
      return client.post<{ ok: true }>('/api/v1/staff/auth/forgot-password', body, {
        auth: false,
      });
    },
    resetPassword(body: StaffResetPasswordBody): Promise<{ ok: true }> {
      return client.post<{ ok: true }>('/api/v1/staff/auth/reset-password', body, { auth: false });
    },
    me(): Promise<StaffDto> {
      return client.get<StaffDto>('/api/v1/staff/auth/me');
    },
    logout(refreshToken?: string): Promise<{ ok: true }> {
      return client.post<{ ok: true }>(
        '/api/v1/staff/auth/logout',
        refreshToken ? { refreshToken } : {}
      );
    },
  };
}

export function createStaffApi(client: ApiClient) {
  return {
    dashboard(): Promise<StaffDashboardStatsDto> {
      return client.get('/api/v1/staff/dashboard');
    },
    setPresence(onShift: boolean): Promise<{ onShift: boolean }> {
      return client.patch('/api/v1/staff/me/presence', { onShift });
    },
    myShift(): Promise<MyShiftDto> {
      return client.get('/api/v1/staff/me/shift');
    },
    startShift(): Promise<StartShiftResponse> {
      return client.post('/api/v1/staff/me/shift/start', {});
    },
    finishShift(body: FinishShiftBody): Promise<FinishShiftResponse> {
      return client.post('/api/v1/staff/me/shift/finish', body);
    },
    notificationSettings(): Promise<StaffNotificationStateDto> {
      return client.get('/api/v1/staff/me/notifications');
    },
    updateNotificationSettings(
      body: UpdateStaffNotificationSettingsBody
    ): Promise<StaffNotificationStateDto> {
      return client.patch('/api/v1/staff/me/notifications', body);
    },
    linkTelegram(): Promise<StaffTelegramLinkDto> {
      return client.post('/api/v1/staff/me/notifications/telegram/link', {});
    },
    unlinkTelegram(): Promise<StaffNotificationStateDto> {
      return client.post('/api/v1/staff/me/notifications/telegram/unlink', {});
    },
    cases(query?: { stage?: string; search?: string }): Promise<CaseSummaryDto[]> {
      const qs = new URLSearchParams();
      if (query?.stage) qs.set('stage', query.stage);
      if (query?.search) qs.set('search', query.search);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return client.get(`/api/v1/staff/cases${suffix}`);
    },
    caseDetail(id: string): Promise<CaseDetailDto> {
      return client.get(`/api/v1/staff/cases/${id}`);
    },
    updateCase(id: string, body: UpdateCaseBody): Promise<CaseDetailDto> {
      return client.patch(`/api/v1/staff/cases/${id}`, body);
    },
    saveCaseDraft(id: string, body: SaveCaseDraftBody): Promise<CaseDetailDto> {
      return client.post(`/api/v1/staff/cases/${id}/draft`, body);
    },
    templates(search?: string): Promise<ResponseTemplateDto[]> {
      const suffix = search ? `?search=${encodeURIComponent(search)}` : '';
      return client.get(`/api/v1/staff/templates${suffix}`);
    },
    createTemplate(body: CreateResponseTemplateBody): Promise<ResponseTemplateDto> {
      return client.post('/api/v1/staff/templates', body);
    },
    updateTemplate(id: string, body: UpdateResponseTemplateBody): Promise<ResponseTemplateDto> {
      return client.patch(`/api/v1/staff/templates/${id}`, body);
    },
    deleteTemplate(id: string): Promise<{ ok: true }> {
      return client.delete(`/api/v1/staff/templates/${id}`);
    },
    useTemplate(id: string): Promise<ResponseTemplateDto> {
      return client.post(`/api/v1/staff/templates/${id}/use`, {});
    },
    lawyerChangeRequests(query?: LawyerChangeRequestListQuery): Promise<LawyerChangeRequestListDto> {
      const qs = new URLSearchParams();
      if (query?.status && query.status !== 'all') qs.set('status', query.status);
      if (query?.page) qs.set('page', String(query.page));
      if (query?.pageSize) qs.set('pageSize', String(query.pageSize));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return client.get(`/api/v1/staff/lawyer-change-requests${suffix}`);
    },
    lawyerChangeRequest(id: string): Promise<LawyerChangeRequestDto> {
      return client.get(`/api/v1/staff/lawyer-change-requests/${id}`);
    },
    approveLawyerChange(id: string, body: ApproveLawyerChangeRequestBody): Promise<LawyerChangeRequestDto> {
      return client.post(`/api/v1/staff/lawyer-change-requests/${id}/approve`, body);
    },
    rejectLawyerChange(id: string, body: RejectLawyerChangeRequestBody): Promise<LawyerChangeRequestDto> {
      return client.post(`/api/v1/staff/lawyer-change-requests/${id}/reject`, body);
    },
    shifts(query?: ShiftListQuery): Promise<ShiftListDto> {
      const qs = new URLSearchParams();
      if (query?.staffId) qs.set('staffId', query.staffId);
      if (query?.from) qs.set('from', query.from);
      if (query?.to) qs.set('to', query.to);
      if (query?.status && query.status !== 'all') qs.set('status', query.status);
      if (query?.page) qs.set('page', String(query.page));
      if (query?.pageSize) qs.set('pageSize', String(query.pageSize));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return client.get(`/api/v1/staff/shifts${suffix}`);
    },
    availableLawyers(): Promise<AvailableLawyerDto[]> {
      return client.get('/api/v1/staff/lawyers/available');
    },
    listClients(params?: { search?: string; page?: number }): Promise<{
      data: ClientDto[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }> {
      const qs = new URLSearchParams();
      if (params?.search) qs.set('search', params.search);
      if (params?.page) qs.set('page', String(params.page));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return client.get(`/api/v1/staff/clients${suffix}`);
    },
    getClient(id: string): Promise<ClientDto> {
      return client.get(`/api/v1/staff/clients/${id}`);
    },
    clientHistory(id: string): Promise<ClientAssignmentHistoryDto[]> {
      return client.get(`/api/v1/staff/clients/${id}/history`);
    },
    claimClient(id: string): Promise<unknown> {
      return client.post(`/api/v1/staff/clients/${id}/claim`, {});
    },
    reassignClient(id: string, body: ReassignLawyerBody): Promise<unknown> {
      return client.post(`/api/v1/staff/clients/${id}/reassign`, body);
    },
    requests(status?: string): Promise<unknown[]> {
      const suffix = status ? `?status=${encodeURIComponent(status)}` : '';
      return client.get(`/api/v1/staff/requests${suffix}`);
    },
    updateRequest(id: string, body: Record<string, unknown>): Promise<unknown> {
      return client.patch(`/api/v1/staff/requests/${id}`, body);
    },
    articles(): Promise<unknown[]> {
      return client.get('/api/v1/staff/articles');
    },
    createArticle(body: Record<string, unknown>): Promise<unknown> {
      return client.post('/api/v1/staff/articles', body);
    },
    updateArticle(id: string, body: Record<string, unknown>): Promise<unknown> {
      return client.patch(`/api/v1/staff/articles/${id}`, body);
    },
    deleteArticle(id: string): Promise<unknown> {
      return client.delete(`/api/v1/staff/articles/${id}`);
    },
    faq(): Promise<unknown[]> {
      return client.get('/api/v1/staff/faq');
    },
    createFaq(body: Record<string, unknown>): Promise<unknown> {
      return client.post('/api/v1/staff/faq', body);
    },
    updateFaq(id: string, body: Record<string, unknown>): Promise<unknown> {
      return client.patch(`/api/v1/staff/faq/${id}`, body);
    },
    deleteFaq(id: string): Promise<unknown> {
      return client.delete(`/api/v1/staff/faq/${id}`);
    },
    tariffs(): Promise<unknown[]> {
      return client.get('/api/v1/staff/tariffs');
    },
    createTariff(body: Record<string, unknown>): Promise<unknown> {
      return client.post('/api/v1/staff/tariffs', body);
    },
    updateTariff(id: string, body: Record<string, unknown>): Promise<unknown> {
      return client.patch(`/api/v1/staff/tariffs/${id}`, body);
    },
    deleteTariff(id: string): Promise<unknown> {
      return client.delete(`/api/v1/staff/tariffs/${id}`);
    },
    steps(): Promise<unknown[]> {
      return client.get('/api/v1/staff/steps');
    },
    createStep(body: Record<string, unknown>): Promise<unknown> {
      return client.post('/api/v1/staff/steps', body);
    },
    updateStep(id: string, body: Record<string, unknown>): Promise<unknown> {
      return client.patch(`/api/v1/staff/steps/${id}`, body);
    },
    deleteStep(id: string): Promise<unknown> {
      return client.delete(`/api/v1/staff/steps/${id}`);
    },
    settings(): Promise<Record<string, unknown>> {
      return client.get('/api/v1/staff/settings');
    },
    updateSettings(body: Record<string, unknown>): Promise<unknown> {
      return client.patch('/api/v1/staff/settings', body);
    },
    team(): Promise<unknown[]> {
      return client.get('/api/v1/staff/team');
    },
    updateTeamMember(id: string, body: Record<string, unknown>): Promise<unknown> {
      return client.patch(`/api/v1/staff/team/${id}`, body);
    },
    inviteTeamMember(body: Record<string, unknown>): Promise<unknown> {
      return client.post('/api/v1/staff/team/invite', body);
    },
    roles(): Promise<unknown[]> {
      return client.get('/api/v1/staff/roles');
    },
    permissions(): Promise<unknown[]> {
      return client.get('/api/v1/staff/permissions');
    },
    updateRole(key: string, body: Record<string, unknown>): Promise<unknown> {
      return client.patch(`/api/v1/staff/roles/${key}`, body);
    },
    audit(): Promise<unknown[]> {
      return client.get('/api/v1/staff/audit');
    },
    analytics(): Promise<Record<string, unknown>> {
      return client.get('/api/v1/staff/analytics');
    },
  };
}
